import { useState } from 'react';
import { Info, MapPin, CheckCircle2 } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { jobService } from '../../../services/jobService';
import ClientSelect from '../ClientSelect';
import JobSiteSelect from '../JobSiteSelect';

export default function BasicInfoTab({
  formData,
  onChange,
  errors,
  companyId,
  jobDisplayId
}) {
  const [jobNameSuggestions, setJobNameSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Search for job name suggestions
  const handleJobNameChange = async (e) => {
    const value = e?.target?.value;
    onChange?.({ jobName: value });

    if (value?.length >= 2 && companyId) {
      try {
        const suggestions = await jobService.searchJobNames(companyId, value);
        setJobNameSuggestions(suggestions || []);
        setShowSuggestions(suggestions?.length > 0);
      } catch (err) {
        console.error('Failed to fetch job name suggestions:', err);
      }
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    onChange?.({ jobName: suggestion });
    setShowSuggestions(false);
  };

  // Handle client selection
  const handleClientChange = (client) => {
    onChange?.({
      client,
      clientId: client?.id || null,
      // Clear job site when client changes
      jobSite: null,
      jobSiteId: null,
      // Clear address
      originalAddress: '',
      locationLat: null,
      locationLng: null
    });
  };

  // Handle job site selection
  const handleJobSiteChange = (jobSite) => {
    onChange?.({
      jobSite,
      jobSiteId: jobSite?.id || null
    });
  };

  // Handle address change from job site selection
  const handleAddressChange = (addressData) => {
    if (addressData) {
      onChange?.({
        originalAddress: addressData.address || '',
        locationLat: addressData.lat || null,
        locationLng: addressData.lng || null
      });
    } else {
      onChange?.({
        originalAddress: '',
        locationLat: null,
        locationLng: null
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Job ID (Auto-generated, read-only) */}
      <div>
        <label className="block text-sm font-medium text-white-shade-1 mb-1">
          Job ID
        </label>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={jobDisplayId || 'Will be generated on save'}
            readOnly
            className="flex-1 px-3 py-2 bg-surface border border-border rounded-lg text-text-secondary cursor-not-allowed"
          />
          <div className="flex items-center gap-1 text-xs text-white-shade-2">
            <Info className="w-3 h-3" />
            Auto-generated
          </div>
        </div>
      </div>

      {/* Job Name with Autocomplete */}
      <div className="relative">
        <label className="block text-sm font-medium text-white-shade-1 mb-1">
          Job Name <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          value={formData?.jobName || ''}
          onChange={handleJobNameChange}
          onFocus={() => jobNameSuggestions?.length > 0 && setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder="e.g., Office Deep Clean, Weekly Maintenance"
          className={cn(
            'w-full px-3 py-2 bg-white border rounded-lg text-background placeholder-gray-500 focus:outline-none focus:border-primary-orange',
            errors?.jobName ? 'border-red-500' : 'border-charcoal-shade-2'
          )}
        />
        {errors?.jobName && (
          <p className="mt-1 text-xs text-red-400">{errors.jobName}</p>
        )}

        {/* Suggestions Dropdown */}
        {showSuggestions && jobNameSuggestions?.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-surface border border-border rounded-lg shadow-lg max-h-40 overflow-y-auto">
            {jobNameSuggestions?.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleSelectSuggestion(suggestion)}
                className="w-full px-4 py-2 text-left text-sm text-text-secondary hover:bg-surface-light transition-colors first:rounded-t-lg last:rounded-b-lg"
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Client Select */}
      <div>
        <label className="block text-sm font-medium text-white-shade-1 mb-1">
          Client <span className="text-red-400">*</span>
        </label>
        <ClientSelect
          value={formData?.client}
          onChange={handleClientChange}
          companyId={companyId}
          placeholder="Search or select a client..."
          error={errors?.client}
        />
      </div>

      {/* Job Site Select */}
      <div>
        <label className="block text-sm font-medium text-white-shade-1 mb-1">
          Job Site <span className="text-red-400">*</span>
        </label>
        <JobSiteSelect
          value={formData?.jobSite}
          onChange={handleJobSiteChange}
          companyId={companyId}
          clientId={formData?.clientId}
          placeholder="Search or select a job site..."
          onAddressChange={handleAddressChange}
          error={errors?.jobSite}
        />
      </div>

      {/* Address Display (View-only from Job Site) */}
      {formData?.originalAddress && (
        <div className="p-4 bg-surface rounded-lg border border-border">
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-text-primary mb-1">
                Job Location
              </p>
              <p className="text-sm text-text-secondary">
                {formData.originalAddress}
              </p>
              {formData?.locationLat && formData?.locationLng && (
                <p className="text-xs text-accent mt-1 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Location verified ({formData.locationLat.toFixed(4)}, {formData.locationLng.toFixed(4)})
                </p>
              )}
            </div>
          </div>
          <p className="text-xs text-white-shade-2 mt-2 pl-8">
            Address is determined by the selected job site and cannot be edited here.
          </p>
        </div>
      )}

      {/* Priority */}
      <div>
        <label className="block text-sm font-medium text-white-shade-1 mb-2">
          Priority
        </label>
        <div className="flex gap-4">
          {['low', 'medium', 'high'].map((priority) => (
            <label
              key={priority}
              className={cn(
                'flex-1 p-3 rounded-lg border cursor-pointer transition-colors text-center',
                formData?.priority === priority
                  ? priority === 'high'
                    ? 'border-red-500'
                    : priority === 'medium'
                      ? 'border-yellow-500'
                      : 'border-gray-400'
                  : 'border-border'
              )}
            >
              <input
                type="radio"
                name="priority"
                value={priority}
                checked={formData?.priority === priority}
                onChange={(e) => onChange?.({ priority: e?.target?.value })}
                className="sr-only"
              />
              <span className={cn(
                'text-sm font-medium capitalize',
                formData?.priority === priority
                  ? priority === 'high'
                    ? 'text-red-400'
                    : priority === 'medium'
                      ? 'text-yellow-400'
                      : 'text-text-secondary'
                  : 'text-white-shade-1'
              )}>
                {priority}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-white-shade-1 mb-1">
          Description
        </label>
        <textarea
          value={formData?.description || ''}
          onChange={(e) => onChange?.({ description: e?.target?.value })}
          placeholder="Describe the scope of work..."
          rows={4}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-background placeholder-gray-500 focus:outline-none focus:border-primary-orange resize-none"
        />
      </div>
    </div>
  );
}
